import { onScopeDispose, ref } from 'vue'

/**
 * Copy-to-clipboard with a short-lived "copied" flag for button affordances.
 *
 * Lifted from TextToClipboard.vue so panels that need a copy action without
 * that component's truncating span can reuse the same behaviour, including the
 * hidden-textarea fallback for browsers that don't expose the async clipboard
 * API over insecure origins.
 */
export function useCopyToClipboard(resetAfterMs = 1200) {
  const copied = ref(false)
  let resetTimer: ReturnType<typeof setTimeout> | undefined

  // Insecure origins hide the async API entirely, but a secure one can still
  // reject the write (permission denied, document not focused), so the
  // textarea path has to be reachable from both.
  function copyViaTextarea(text: string) {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'absolute'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }

  async function copy(value: string | number | null | undefined) {
    const text = String(value ?? '')
    if (!text) return

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text)
      } catch {
        copyViaTextarea(text)
      }
    } else {
      copyViaTextarea(text)
    }

    copied.value = true
    // Restart the window on repeat copies so the flag doesn't clear early.
    if (resetTimer) clearTimeout(resetTimer)
    resetTimer = setTimeout(() => {
      copied.value = false
    }, resetAfterMs)
  }

  // A copy landing just before teardown would otherwise leave a timer writing
  // to a discarded ref.
  // failSilently: the composable is also legitimately callable outside a
  // component scope (tests, plain modules), where there is nothing to dispose.
  onScopeDispose(() => {
    if (resetTimer) clearTimeout(resetTimer)
  }, true)

  return { copied, copy }
}
